import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  AlertTriangle,
  BarChart3,
  ClipboardList,
  PencilLine,
  SlidersHorizontal,
} from 'lucide-react';
import { updateStock } from '../../store';
import '../../pages/ProductManagement.css';
import './StockAdjustment.css';

const iconProps = { size: 18, strokeWidth: 1.75 };

const getStockBadgeClass = (stock, moq = 100) => {
  if (stock === 0) return 'pm-stock-out';
  if (stock <= moq) return 'pm-stock-low';
  return 'pm-stock-in';
};

const StockAdjustment = ({ product, onComplete, onClose }) => {
  const dispatch = useDispatch();

  const [operation, setOperation] = useState('add'); // 'add', 'subtract', 'set'
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!quantity || isNaN(quantity) || Number(quantity) <= 0) {
      newErrors.quantity = 'Please enter a valid positive number';
    }

    if (!reason.trim()) {
      newErrors.reason = 'Reason is required for audit purposes';
    }

    // Additional validation for subtract operation
    if (operation === 'subtract') {
      const newStock = product.stock - Number(quantity);
      if (newStock < 0) {
        newErrors.quantity = `Cannot subtract ${quantity}. Only ${product.stock} units available.`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await dispatch(updateStock({
        productId: product._id,
        quantity: Number(quantity),
        operation,
        reason
      })).unwrap();

      onComplete();
    } catch (error) {
      const msg =
        (typeof error === 'string' && error) ||
        (error?.error && typeof error.error === 'object' && error.error.message) ||
        (error?.error && typeof error.error === 'string' && error.error) ||
        error?.message ||
        'Failed to update stock. Please try again.';
      setErrors({ submit: msg });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getOperationLabel = () => {
    switch (operation) {
      case 'add':
        return 'Add to Stock';
      case 'subtract':
        return 'Remove from Stock';
      case 'set':
        return 'Set Stock Level';
      default:
        return 'Adjust Stock';
    }
  };

  const getNewStockValue = () => {
    if (!quantity || isNaN(quantity)) return product.stock;

    const qty = Number(quantity);
    switch (operation) {
      case 'add':
        return product.stock + qty;
      case 'subtract':
        return Math.max(0, product.stock - qty);
      case 'set':
        return qty;
      default:
        return product.stock;
    }
  };

  const getStockDiff = () => {
    const newStock = getNewStockValue();
    const diff = newStock - product.stock;
    if (diff > 0) return `+${diff}`;
    if (diff < 0) return `${diff}`;
    return '0';
  };

  return (
    <div className="pm-modal-overlay" onClick={onClose}>
      <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pm-modal-header">
          <h2 className="pm-modal-title">Stock Adjustment</h2>
          <button type="button" className="pm-modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <form className="sa-stock-form" onSubmit={handleSubmit}>
          <div className="pm-modal-content">
            {/* Product summary — same section pattern as Product Details */}
            <div className="pm-product-detail-section">
              <h3 className="pm-section-title">
                <ClipboardList {...iconProps} className="pm-section-title-icon" aria-hidden />
                Product
              </h3>
              <div className="pm-detail-grid">
                <div className="pm-detail-item">
                  <div className="pm-detail-label">Product Name</div>
                  <div className="pm-detail-value">{product.name}</div>
                </div>
                {product._id && (
                  <div className="pm-detail-item">
                    <div className="pm-detail-label">Product ID</div>
                    <div className="pm-detail-value sa-mono-id">{String(product._id)}</div>
                  </div>
                )}
                <div className="pm-detail-item">
                  <div className="pm-detail-label">Current Stock</div>
                  <div className="pm-detail-value">
                    <span className={`pm-stock-badge ${getStockBadgeClass(product.stock, product.moq)}`}>
                      {product.stock} units
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Operation Selection */}
            <div className="pm-product-detail-section">
              <h3 className="pm-section-title">
                <SlidersHorizontal {...iconProps} className="pm-section-title-icon" aria-hidden />
                Operation Type
              </h3>
              <div className="sa-operation-buttons">
                <button
                  type="button"
                  className={`sa-operation-btn ${operation === 'add' ? 'sa-active sa-add' : ''}`}
                  onClick={() => setOperation('add')}
                  aria-label="Add to stock"
                >
                  <span className="sa-operation-icon">+</span>
                  <span className="sa-operation-label">Add Stock</span>
                </button>
                <button
                  type="button"
                  className={`sa-operation-btn ${operation === 'subtract' ? 'sa-active sa-subtract' : ''}`}
                  onClick={() => setOperation('subtract')}
                  aria-label="Remove from stock"
                >
                  <span className="sa-operation-icon">−</span>
                  <span className="sa-operation-label">Remove Stock</span>
                </button>
                <button
                  type="button"
                  className={`sa-operation-btn ${operation === 'set' ? 'sa-active sa-set' : ''}`}
                  onClick={() => setOperation('set')}
                  aria-label="Set stock level"
                >
                  <span className="sa-operation-icon">=</span>
                  <span className="sa-operation-label">Set Level</span>
                </button>
              </div>
            </div>

            {/* Quantity Input */}
            <div className="pm-product-detail-section">
              <h3 className="pm-section-title">
                <PencilLine {...iconProps} className="pm-section-title-icon" aria-hidden />
                Adjustment
              </h3>
              <div className="sa-form-stack">
                <label className="pm-detail-label sa-form-label-block" htmlFor="sa-adjust-qty">
                  {operation === 'set' ? 'New Stock Level' : 'Quantity'} <span className="sa-required">*</span>
                </label>
                <input
                  id="sa-adjust-qty"
                  type="number"
                  className={`sa-form-input ${errors.quantity ? 'sa-error' : ''}`}
                  value={quantity}
                  onChange={(e) => {
                    setQuantity(e.target.value);
                    if (errors.quantity) {
                      setErrors(prev => ({ ...prev, quantity: '' }));
                    }
                  }}
                  placeholder={operation === 'set' ? 'Enter new stock level' : 'Enter quantity'}
                  min="0"
                />
                {errors.quantity && <span className="sa-error-text">{errors.quantity}</span>}

                <label className="pm-detail-label sa-form-label-block sa-label-mt" htmlFor="sa-adjust-reason">
                  Reason <span className="sa-required">*</span>
                </label>
                <textarea
                  id="sa-adjust-reason"
                  className={`sa-form-textarea ${errors.reason ? 'sa-error' : ''}`}
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    if (errors.reason) {
                      setErrors(prev => ({ ...prev, reason: '' }));
                    }
                  }}
                  placeholder="e.g., New stock arrival, Damaged goods, Inventory correction..."
                  rows="3"
                />
                {errors.reason && <span className="sa-error-text">{errors.reason}</span>}
              </div>
            </div>

            {/* Preview */}
            {quantity && !errors.quantity && (
              <div className="pm-product-detail-section">
                <h3 className="pm-section-title">
                  <BarChart3 {...iconProps} className="pm-section-title-icon" aria-hidden />
                  Stock Change Preview
                </h3>
                <div className="pm-detail-grid sa-preview-grid">
                  <div className="pm-detail-item">
                    <div className="pm-detail-label">Current Stock</div>
                    <div className="pm-detail-value">{product.stock} units</div>
                  </div>
                  <div className="pm-detail-item">
                    <div className="pm-detail-label">Change</div>
                    <div className={`pm-detail-value ${operation === 'add' || operation === 'set' ? 'sa-positive' : 'sa-negative'}`}>
                      {getStockDiff()} units
                    </div>
                  </div>
                  <div className="pm-detail-item sa-preview-span">
                    <div className="pm-detail-label">New Stock Level</div>
                    <div className="pm-detail-value sa-preview-emphasis">{getNewStockValue()} units</div>
                  </div>
                </div>
              </div>
            )}

            {errors.submit && (
              <div className="sa-submit-error" role="alert">
                <AlertTriangle {...iconProps} className="sa-submit-error-icon" aria-hidden />
                {errors.submit}
              </div>
            )}
          </div>

          <div className="pm-modal-actions">
            <button
              type="button"
              className="pm-btn-cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="pm-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Updating...' : getOperationLabel()}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StockAdjustment;
